import { useState, type ReactNode } from "react";

import { AuthScreen } from "~/components/composed/AuthScreen";
import { AppText, Button, ButtonText, Stack, TextField } from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { useMfaState } from "~/lib/totp";

function MfaChallenge() {
  const { logout, completeMfaSignIn } = useAuth();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await (recovery
        ? completeMfaSignIn({ recoveryCode: code })
        : completeMfaSignIn({ code }));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "That code did not work.",
      );
      setBusy(false);
    }
  };

  return (
    <AuthScreen
      title="Two-factor authentication"
      subtitle={
        recovery
          ? "Enter one of your recovery codes."
          : "Enter the 6-digit code from your authenticator app."
      }
    >
      <Stack>
        {error ? <AppText tone="danger">{error}</AppText> : null}
        <TextField
          accessibilityLabel={recovery ? "Recovery code" : "6-digit code"}
          value={code}
          onChangeText={setCode}
          keyboardType={recovery ? "default" : "number-pad"}
          autoCapitalize={recovery ? "characters" : "none"}
          autoComplete="one-time-code"
          maxLength={recovery ? 11 : 6}
          centered
        />
        <Button
          disabled={busy || code.trim().length < (recovery ? 10 : 6)}
          onPress={() => void submit()}
        >
          <ButtonText>{busy ? "Verifying…" : "Verify"}</ButtonText>
        </Button>
        <Button
          variant="outline"
          onPress={() => {
            setRecovery((value) => !value);
            setCode("");
            setError("");
          }}
        >
          <ButtonText>
            {recovery ? "Use authenticator code" : "Use a recovery code"}
          </ButtonText>
        </Button>
        <Button variant="outline" onPress={() => void logout()}>
          <ButtonText>Sign out</ButtonText>
        </Button>
      </Stack>
    </AuthScreen>
  );
}

/**
 * Holds the app back until a signed-in person who turned on two-factor
 * authentication has entered their code. Everyone else passes straight through.
 */
export function MfaGate({ children }: { children: ReactNode }) {
  const state = useMfaState();
  if (state === "checking") return null;
  if (state === "required") return <MfaChallenge />;
  return <>{children}</>;
}
